import {combine, restore} from 'effector';

import {createGate} from 'effector-react';

import {fetchGdsGrid} from 'api';
import {
    createGds,
    fetchGdsDetail,
    getCustoms,
    GoodsDeclarationDetails,
    saveChangesGdsCodeList,
    saveChangesGdsInfo,
    submitGds,
} from 'api/documents/documents';
import {GdsStatus, ProductStatus} from 'core/dictionaries';
import {createGridTableModel} from 'core/hooks/grid2.0';
import {rootDomain} from 'core/rootDomain';
import {GdsGridRawRow} from 'types';
import {GdsSimplePaginationContent} from 'types/api';

const domain = rootDomain.createDomain('DocumentsGridTableDomain');

export const fetchGdsGridFx = domain.createEffect({
    name: 'fetchGdsGridFx',
    handler: fetchGdsGrid,
});

export const fetchGdsDetailFx = domain.createEffect({
    name: 'fetchGdsDetailFx',
    handler: fetchGdsDetail,
});

export const createGdsFx = domain.createEffect({
    name: 'createGdsFx',
    handler: createGds,
});

export const fetchCustomsFx = domain.createEffect({
    name: 'fetchCustomsFx',
    handler: getCustoms,
});

export const submitGdsFx = domain.createEffect({
    name: 'submitGdsFx',
    handler: submitGds,
});

export const saveChangesGdsInfoFx = domain.createEffect({
    name: 'saveChangesGdsInfoFx',
    handler: async ({
        id,
        params,
    }: {
        id: string;
        params: {customsId: number; storageLocation: string};
    }) => saveChangesGdsInfo(id, params),
});

export const saveChangesGdsCodeListFx = domain.createEffect({
    name: 'saveChangesGdsCodeListFx',
    handler: async ({id, params}: {id: string; params: {[key: string]: boolean}}) =>
        saveChangesGdsCodeList(id, params),
});

export const saveGdsChangesFx = domain.createEffect({
    name: 'saveGdsChangesFx',
    handler: async ({
        gdsId,
        hasInfoChanges,
        hasCodeListChanges,
        infoParams,
        codeListParams,
    }: {
        gdsId: string;
        hasInfoChanges: boolean;
        hasCodeListChanges: boolean;
        infoParams?: {customsId: number; storageLocation: string};
        codeListParams?: {[key: string]: boolean};
    }) => {
        if (!hasInfoChanges && !hasCodeListChanges) {
            console.log('No changes to save');
            return {success: true, message: 'No changes'};
        }

        const promises: Array<Promise<any>> = [];
        const labels: string[] = [];

        if (hasInfoChanges && infoParams) {
            promises.push(
                saveChangesGdsInfoFx({
                    id: gdsId,
                    params: infoParams,
                }),
            );
            labels.push('info');
        }

        if (hasCodeListChanges && codeListParams) {
            promises.push(
                saveChangesGdsCodeListFx({
                    id: gdsId,
                    params: codeListParams,
                }),
            );
            labels.push('codeList');
        }

        const results = await Promise.allSettled(promises);

        const errors: string[] = [];
        results.forEach((result, index) => {
            if (result.status === 'rejected') {
                errors.push(labels[index]);
            }
        });

        if (errors.length > 0) {
            const errorTabIndices: number[] = [];
            if (errors.includes('info')) errorTabIndices.push(0);
            if (errors.includes('codeList')) errorTabIndices.push(1);

            const error = new Error(`Failed to save: ${errors.join(', ')}`);
            (error as any).errorTabs = errorTabIndices;
            (error as any).rejectedReasons = results
                .filter((r) => r.status === 'rejected')
                .map((r: any) => r.reason);

            throw error;
        }

        return {success: true, results};
    },
});

export const {
    filters$,
    filtersChanged,
    filtersReset,
    defaultFilters,
    sortings$,
    sortingsChanged,
    sortingsReset,
    defaultSortings,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    Gate,
    tableStateChanged,
    tableStateCacheChanged,
    stateReset,
} = createGridTableModel({
    domain,
});

export const gdsDetailGate = createGate<{id: string}>('gdsDetailGate');

export const data$ = domain.createStore<GdsSimplePaginationContent<GdsGridRawRow> | null>(null);

export const searchChanged = domain.createEvent<string>();
export const search$ = domain
    .createStore<string>('')
    .on(searchChanged, (_, value) => value)
    .reset(Gate.close);

export const refetchWithSortByDescDate = domain.createEvent();

export const gridCacheChanged = domain.createEvent<{pageNumber: number; token: string | null}>();
export const gridCacheReset = domain.createEvent();
export const gridTokensCache$ = domain.createStore<Record<number, string | null>>({0: null});

export const gdsCreatingStepChanged = domain.createEvent<number>();
export const gdsCreatingStep$ = restore(gdsCreatingStepChanged, 0).reset(gdsDetailGate.close);

export const tableState$ = combine({
    data: data$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    sortings: sortings$,
    search: search$,
    isLoading: fetchGdsGridFx.pending,
});

export const handleErrorTabs = domain.createEvent<number[]>();
export const errorTabs$ = domain.createStore<number[]>([]);

export const gdsIdChanged = domain.createEvent<string>();
export const gdsId$ = restore(gdsIdChanged, null).reset(gdsDetailGate.close);

export const gdsDetail$ = domain.createStore<GoodsDeclarationDetails | null>(null);
export const gdsStatus$ = gdsDetail$.map((detail) => detail?.status ?? GdsStatus.DRAFT);

export const customsNameAndNumber$ = gdsDetail$.map((detail) => ({
    label: detail?.customsId ?? '',
    value: detail?.customsId ?? '',
    number: detail?.customsId?.toString() ?? '',
}));

export const updateCustomsNameAndNumber = domain.createEvent<{
    label: string;
    value: number;
    number: string;
}>();

export const gdsStorageLocation$ = gdsDetail$.map((detail) => ({
    label: detail?.storageLocation ?? '',
    value: detail?.storageLocation ?? '',
}));
export const updateGdsStorageLocation = domain.createEvent<{label: string; value: string}>();

export const gdsCodeList$ = gdsDetail$.map<{code: string; status: ProductStatus | string}[]>(
    (detail) =>
        detail?.customsMarkingCodeList.map((item) => ({
            code: item.code,
            status: item.status ?? ProductStatus.AGGREGATED,
        })) ?? [],
);
export const updateGdsCodeList = domain.createEvent<{
    data?: {code: string; status: ProductStatus | string};
    action?: 'add' | 'remove';
    index?: number;
}>();

export const saveChangesGds = domain.createEvent();

export const goodsDeclarationDetails$ = combine({
    gdsId: gdsId$,
    gdsStatus: gdsStatus$,
    customsNameAndNumber: customsNameAndNumber$,
    gdsStorageLocation: gdsStorageLocation$,
    gdsCodeList: gdsCodeList$,
    isLoading: fetchGdsDetailFx.pending,
});

export const saveGdsChangesClicked = domain.createEvent();

export const isSaving$ = saveGdsChangesFx.pending;
export const isSubmitting$ = submitGdsFx.pending;
